import { Dialog } from '@angular/cdk/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { DeviceDto } from 'src/app/models/device.model';
import { LicenseBatchDto } from 'src/app/models/license.model';
import { AlertService } from 'src/app/services/alert.service';
import { DeviceService } from 'src/app/services/device.service';
import { LicenseBatchService } from 'src/app/services/license-batch.service';
import { LicenseBatchStore } from 'src/app/services/license-batch.store';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';
import {
  ActionButtonColumnDefinition,
  ColumnDefinition,
  DataColumnDefinition,
  PaginatorConfig,
} from '../qs-table/column-definition.interface';

type DeviceTableRow = {
  no: number;
  id: string;
  alias: string;
  deviceType: string;
  model: string;
  unitId: string;
  company: string;
};

@Component({
  selector: 'app-license-manage-devices',
  templateUrl: './license-manage-devices.component.html',
  styleUrls: ['./license-manage-devices.component.scss'],
  standalone: false,
})
export class LicenseManageDevicesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);
  private readonly alertService = inject(AlertService);
  private readonly licenseBatchService = inject(LicenseBatchService);
  private readonly deviceService = inject(DeviceService);
  protected readonly licenseBatchStore = inject(LicenseBatchStore);
  private readonly profile = toSignal(this.store.select(selectProfileState), {
    requireSync: false,
  });

  batch: LicenseBatchDto | null = null;
  linkedDevicesRows: DeviceTableRow[] = [];
  availableDevicesRows: DeviceTableRow[] = [];
  loading = false;
  loadingCompanyDevices = false;
  private readonly processingDeviceIds = new Set<string>();

  readonly isSearchEnabled = true;
  readonly isPaginatorEnabled = true;
  readonly paginatorConfig: PaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [5, 10, 15, 25],
  };

  readonly deviceColumns: ColumnDefinition<DeviceTableRow>[] = [
    {
      type: 'data',
      field: 'no',
      header: 'No',
      sortFn: (a, b, multiplier) => (a.no - b.no) * multiplier,
    } as DataColumnDefinition<DeviceTableRow>,
    {
      type: 'data',
      field: 'id',
      header: 'Device ID',
      sortFn: (a, b, multiplier) => a.id.localeCompare(b.id) * multiplier,
    } as DataColumnDefinition<DeviceTableRow>,
    {
      type: 'data',
      field: 'alias',
      header: 'Alias',
      sortFn: (a, b, multiplier) => a.alias.localeCompare(b.alias) * multiplier,
    } as DataColumnDefinition<DeviceTableRow>,
    {
      type: 'data',
      field: 'deviceType',
      header: 'Type',
      sortFn: (a, b, multiplier) =>
        a.deviceType.localeCompare(b.deviceType) * multiplier,
    } as DataColumnDefinition<DeviceTableRow>,
    {
      type: 'data',
      field: 'model',
      header: 'Model',
      sortFn: (a, b, multiplier) => a.model.localeCompare(b.model) * multiplier,
    } as DataColumnDefinition<DeviceTableRow>,
    {
      type: 'data',
      field: 'unitId',
      header: 'Unit ID',
      sortFn: (a, b, multiplier) =>
        a.unitId.localeCompare(b.unitId) * multiplier,
    } as DataColumnDefinition<DeviceTableRow>,
    {
      type: 'data',
      field: 'company',
      header: 'Company',
      sortFn: (a, b, multiplier) =>
        a.company.localeCompare(b.company) * multiplier,
    } as DataColumnDefinition<DeviceTableRow>,
  ];

  readonly linkedColumns: ColumnDefinition<DeviceTableRow>[] = [
    ...this.deviceColumns,
    {
      type: 'action',
      actionType: 'button',
      header: 'Unlink',
      iconName: 'link_off',
      buttonName: 'Unlink Device',
      buttonCallback: (row) => this.delinkDevice(row),
      isDisabled: (row) => this.isProcessingRow(row),
    } as ActionButtonColumnDefinition<DeviceTableRow>,
  ];

  readonly availableColumns: ColumnDefinition<DeviceTableRow>[] = [
    ...this.deviceColumns,
    {
      type: 'action',
      actionType: 'button',
      header: 'Link',
      iconName: 'link',
      buttonName: 'Link Device',
      buttonCallback: (row) => this.linkDevice(row),
      isDisabled: (row) => this.isProcessingRow(row),
    } as ActionButtonColumnDefinition<DeviceTableRow>,
  ];

  ngOnInit(): void {
    const batchId = this.route.snapshot.paramMap.get('id');
    if (!batchId) {
      this.alertService.error('Missing license batch id.');
      this.navigateBack();
      return;
    }

    this.loading = true;
    this.licenseBatchStore
      .getBatchFromStoreOrFetch({
        batchId,
        projection: 'FULL',
        forceRefresh: true,
      })
      .pipe(take(1))
      .subscribe({
        next: (batch) => {
          if (batch.product?.target === 'USER') {
            this.alertService.error('This license batch targets users.');
            this.batch = batch;
            this.navigateBack();
            return;
          }
          this.batch = batch;
          this.linkedDevicesRows = this.mapLinkedDevicesRows(batch);
          this.loadCompanyDevices(batch);
          this.loading = false;
        },
        error: (error: unknown) => {
          this.loading = false;
          this.alertService.error(this.getErrorMessage(error));
          this.navigateBack();
        },
      });
  }

  get isMasterAdmin(): boolean {
    return this.profile()?.isMasteradmin ?? false;
  }

  private get isAdminRoute(): boolean {
    return this.router.url.includes('/admin/');
  }

  get detailsTitle(): string {
    return 'Manage License Devices';
  }

  get batchTitle(): string {
    return this.batch?.name?.trim() || 'License Batch';
  }

  get batchId(): string {
    return this.batch?.id ?? '-';
  }

  get linkedDevicesCount(): number {
    return this.linkedDevicesRows.length;
  }

  get availableDevicesCount(): number {
    return this.availableDevicesRows.length;
  }

  get companyLabel(): string {
    return this.batch?.company?.name || this.batch?.companyId || 'Not assigned';
  }

  get productLabel(): string {
    const name = this.batch?.product?.name?.trim();
    const id = this.batch?.licensedProductId?.trim();
    if (name) {
      return name;
    }
    if (id) {
      return id;
    }
    return 'Not assigned';
  }

  get scopeLabel(): string {
    const scopeName = this.batch?.scope?.name?.trim();
    if (scopeName) {
      return scopeName;
    }
    return 'Not assigned';
  }

  get componentNames(): string[] {
    const rawComponents =
      (this.batch?.product as { licensedComponents?: unknown[] } | undefined)
        ?.licensedComponents ?? [];
    return rawComponents
      .map((component) => {
        if (typeof component === 'string') {
          return component.trim();
        }
        if (component && typeof component === 'object') {
          const typedComponent = component as {
            componentName?: string;
            id?: string;
          };
          return (
            typedComponent.componentName?.trim() ||
            typedComponent.id?.trim() ||
            ''
          );
        }
        return '';
      })
      .filter((name) => !!name);
  }

  formatDate(dateString?: string): string {
    if (!dateString) {
      return 'Not set';
    }

    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) {
      return dateString;
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(parsedDate);
  }

  navigateBack(): void {
    const batchId = this.batch?.id;
    if (!batchId) {
      this.router.navigate([
        this.isAdminRoute ? '/admin/licenses/batches' : '/licenses/batches',
      ]);
      return;
    }

    this.router.navigate([
      this.isAdminRoute ? '/admin/licenses/batches' : '/licenses/batches',
      batchId,
    ]);
  }

  private loadCompanyDevices(batch: LicenseBatchDto): void {
    const companyId =
      batch.companyId?.trim() || batch.company?.id?.trim() || '';

    if (this.isAdminRoute && !companyId) {
      this.availableDevicesRows = [];
      return;
    }

    this.loadingCompanyDevices = true;

    const source$ = this.isAdminRoute
      ? this.deviceService.getDevicesByCompanyId(companyId, 'BRIEF')
      : this.deviceService.getCompanyDevices('BRIEF');

    source$.pipe(take(1)).subscribe({
      next: (result) => {
        const linkedIdSet = new Set(
          this.linkedDevicesRows
            .map((row) => row.id)
            .filter((id) => id !== '-'),
        );

        const companyDevices = (result.devices ?? []).filter((device) => {
          const deviceId = device.deviceId?.trim();
          return (
            !!deviceId &&
            !linkedIdSet.has(deviceId) &&
            this.hasLicenseManagement(device)
          );
        });

        this.availableDevicesRows = companyDevices.map((device, index) =>
          this.toDeviceRow(device.deviceId?.trim() || '-', device, index),
        );

        this.loadingCompanyDevices = false;
      },
      error: (error: unknown) => {
        this.availableDevicesRows = [];
        this.loadingCompanyDevices = false;
        this.alertService.error(this.getErrorMessage(error));
      },
    });
  }

  private isProcessingRow(row: DeviceTableRow): boolean {
    return this.processingDeviceIds.has(row.id);
  }

  private hasLicenseManagement(device: DeviceDto): boolean {
    return device.deviceManagementFeatures?.hasLicenses === true;
  }

  private linkDevice(row: DeviceTableRow): void {
    const batchId = this.batch?.id?.trim();
    const deviceId = row.id?.trim();

    if (!batchId || !deviceId || deviceId === '-') {
      return;
    }

    if (this.processingDeviceIds.has(deviceId)) {
      return;
    }

    const batchSize = this.batch?.licenseBatchSize ?? null;
    if (
      batchSize !== null &&
      batchSize > 0 &&
      this.linkedDevicesRows.length >= batchSize
    ) {
      this.showBatchSizeReachedModal();
      return;
    }

    this.processingDeviceIds.add(deviceId);
    this.licenseBatchService
      .assignDeviceToBatch(batchId, deviceId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const movedRow =
            this.availableDevicesRows.find(
              (existing) => existing.id === deviceId,
            ) ?? row;

          this.availableDevicesRows = this.resequenceRows(
            this.availableDevicesRows.filter(
              (existing) => existing.id !== deviceId,
            ),
          );
          this.linkedDevicesRows = this.resequenceRows([
            ...this.linkedDevicesRows,
            movedRow,
          ]);
          this.processingDeviceIds.delete(deviceId);
        },
        error: (error: unknown) => {
          this.processingDeviceIds.delete(deviceId);
          this.showActionErrorModal('Failed to link device.', error);
        },
      });
  }

  private showBatchSizeReachedModal(): void {
    this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Batch Size Reached',
        bodyText:
          'You need to delink one device before linking a new device to this batch.',
        cancelable: false,
      },
    });
  }

  private showActionErrorModal(fallbackMessage: string, error?: unknown): void {
    this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Action Failed',
        bodyText: this.getErrorMessage(error) || fallbackMessage,
        cancelable: false,
      },
    });
  }

  private delinkDevice(row: DeviceTableRow): void {
    const batchId = this.batch?.id?.trim();
    const deviceId = row.id?.trim();

    if (!batchId || !deviceId || deviceId === '-') {
      return;
    }

    if (this.processingDeviceIds.has(deviceId)) {
      return;
    }

    this.processingDeviceIds.add(deviceId);
    this.licenseBatchService
      .removeDeviceFromBatch(batchId, deviceId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const movedRow =
            this.linkedDevicesRows.find(
              (existing) => existing.id === deviceId,
            ) ?? row;

          this.linkedDevicesRows = this.resequenceRows(
            this.linkedDevicesRows.filter(
              (existing) => existing.id !== deviceId,
            ),
          );
          this.availableDevicesRows = this.resequenceRows([
            ...this.availableDevicesRows,
            movedRow,
          ]);
          this.processingDeviceIds.delete(deviceId);
        },
        error: (error: unknown) => {
          this.processingDeviceIds.delete(deviceId);
          this.showActionErrorModal('Failed to delink device.', error);
        },
      });
  }

  private resequenceRows(rows: DeviceTableRow[]): DeviceTableRow[] {
    return rows.map((existing, index) => ({
      ...existing,
      no: index + 1,
    }));
  }

  private toDeviceRow(
    deviceId: string,
    device: DeviceDto | undefined,
    index: number,
  ): DeviceTableRow {
    const details = (device?.details ?? null) as {
      type?: string;
      aircraftModel?: {
        name?: string;
        hardwareRevisionString?: string;
      };
      unitId?: number;
    } | null;

    const model =
      device?.deviceSummary?.model?.trim() ||
      device?.deviceSummary?.modelName?.trim() ||
      details?.aircraftModel?.name?.trim() ||
      details?.aircraftModel?.hardwareRevisionString?.trim() ||
      '-';

    return {
      no: index + 1,
      id: deviceId || '-',
      alias:
        device?.nameAlias?.trim() ||
        device?.deviceSummary?.defaultName?.trim() ||
        '-',
      deviceType: device?.deviceType?.trim() || details?.type?.trim() || '-',
      model,
      unitId:
        device?.deviceSummary?.unitId?.trim() ||
        (details?.unitId !== undefined ? String(details.unitId) : '-'),
      company: device?.company?.name?.trim() || '-',
    };
  }

  private mapLinkedDevicesRows(batch: LicenseBatchDto): DeviceTableRow[] {
    const licensedDevices = batch.licensedDevices ?? [];
    const deviceById = new Map<string, DeviceDto>();

    licensedDevices.forEach((device) => {
      const normalizedId = device.deviceId?.trim();
      if (normalizedId) {
        deviceById.set(normalizedId, device);
      }
    });

    const orderedIds: string[] = [];
    const seenIds = new Set<string>();

    (batch.licensedDeviceIds ?? []).forEach((deviceId) => {
      const normalizedId = deviceId?.trim();
      if (!normalizedId || seenIds.has(normalizedId)) {
        return;
      }
      seenIds.add(normalizedId);
      orderedIds.push(normalizedId);
    });

    if (orderedIds.length === 0) {
      deviceById.forEach((_, deviceId) => {
        if (!seenIds.has(deviceId)) {
          seenIds.add(deviceId);
          orderedIds.push(deviceId);
        }
      });
    } else {
      deviceById.forEach((_, deviceId) => {
        if (!seenIds.has(deviceId)) {
          seenIds.add(deviceId);
          orderedIds.push(deviceId);
        }
      });
    }

    return orderedIds.map((deviceId, index) =>
      this.toDeviceRow(deviceId, deviceById.get(deviceId), index),
    );
  }

  private getErrorMessage(error: unknown): string {
    if (!error) {
      return 'Failed to load devices.';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        error?: { message?: string; title?: string };
        message?: string;
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return 'Failed to load devices.';
  }
}
